import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageContext'

export default function Header() {
  const location = useLocation()
  const { lang, setLang, t, getPageUrl, getServiceUrl, getEquivalentPath } = useLanguage()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false)

  // Close mobile menu on location change
  useEffect(() => {
    setMobileMenuOpen(false)
    setDropdownOpen(false)
    setMobileSubmenuOpen(false)
  }, [location])

  const serviceKeys = [
    'specialtransport',
    'containerkoersel',
    'stykgods',
    'traepiller',
    'national-transport',
    'international-transport',
  ]

  const transportSubmenu = serviceKeys.map((key) => ({
    key,
    path: getServiceUrl(key),
    title: t.servicesList[key]?.title || key,
  }))

  const isCurrentActive = (pageKey) => {
    const p = location.pathname
    if (pageKey === 'home') {
      return p === '/' || p === '/en' || p === '/en/'
    }
    if (pageKey === 'services') {
      return p.startsWith('/transport') || p.startsWith('/en/services')
    }
    if (pageKey === 'gallery') {
      return p.startsWith('/galleri') || p.startsWith('/en/gallery')
    }
    if (pageKey === 'about') {
      return p.startsWith('/om-mrou-transport') || p.startsWith('/en/about-us')
    }
    if (pageKey === 'contact') {
      return p.startsWith('/kontakt') || p.startsWith('/en/contact')
    }
    return false
  }

  const daHref = getEquivalentPath('da')
  const enHref = getEquivalentPath('en')

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
      {/* Main Header Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 space-y-3">
        {/* Top Row: Logo & CTAs / Mobile Toggle */}
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={getPageUrl('home')} className="flex items-center gap-3 group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/ed865070-334e-4849-904d-48dc93d1adc7/mrou-transport-logo.png" 
              alt="M.Rou Transport Logo" 
              className="h-8 sm:h-11 md:h-12 w-auto max-w-[150px] sm:max-w-[220px] md:max-w-[260px] object-contain transition-transform group-hover:scale-[1.02]"
            />
          </Link>

          {/* Desktop Header CTA Buttons */}
          <div className="hidden min-[1200px]:flex items-center gap-6">
            {/* Social Media Links */}
            <div className="flex items-center gap-3 border-r border-slate-200 pr-5 text-slate-600">
              <a 
                href="https://www.facebook.com/M.RouTransport/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-sky-600 text-base transition-colors"
              >
                <i className="fa-brands fa-facebook-f"></i>
              </a>
              <a 
                href="https://www.instagram.com/m.rou_transport/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-sky-600 text-base transition-colors"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>
              <a 
                href="https://www.tiktok.com/@mroutransport" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="hover:text-sky-600 text-base transition-colors"
              >
                <i className="fa-brands fa-tiktok"></i>
              </a>
            </div>

            {/* Language Switcher Toggle */}
            <div className="flex items-center border border-slate-200 bg-slate-100 p-0.5 text-xs font-bold shadow-inner" role="group" aria-label={t.nav.switchLang}>
              <Link
                to={daHref}
                onClick={(e) => {
                  if (lang === 'da') e.preventDefault()
                }}
                className={`px-3 py-1.5 transition-all cursor-pointer flex items-center gap-1.5 ${
                  lang === 'da'
                    ? 'bg-sky-600 text-white shadow font-extrabold pointer-events-none'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
                aria-pressed={lang === 'da'}
                title="Skift til Dansk (/)"
              >
                <span>🇩🇰</span>
                <span>DA</span>
              </Link>
              <Link
                to={enHref}
                onClick={(e) => {
                  if (lang === 'en') e.preventDefault()
                }}
                className={`px-3 py-1.5 transition-all cursor-pointer flex items-center gap-1.5 ${
                  lang === 'en'
                    ? 'bg-sky-600 text-white shadow font-extrabold pointer-events-none'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
                aria-pressed={lang === 'en'}
                title="Switch to English (/en/...)"
              >
                <span>🇬🇧</span>
                <span>EN</span>
              </Link>
            </div>

            <a
              href="tel:+4525303016"
              className="flex items-center gap-2 font-bold text-slate-800 hover:text-sky-600 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-600 text-base"></i>
              <span className="whitespace-nowrap text-sm">+45 25 30 30 16</span>
            </a>

            <Link
              to={getPageUrl('contact')}
              className="bg-sky-500 hover:bg-sky-600 active:bg-sky-700 text-white font-bold text-sm px-5 py-2.5 rounded-none shadow-md hover:shadow-lg hover:-translate-y-1 active:translate-y-0 transition-all duration-200 flex items-center gap-2"
            >
              <span>{t.nav.contactCta}</span>
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>

          {/* Mobile Right Bar (Activated under 1200px) */}
          <div className="flex items-center gap-2 sm:gap-3 min-[1200px]:hidden">
            {/* Mobile Language Switcher */}
            <div className="flex items-center border border-slate-200 bg-slate-100 p-0.5 text-[11px] font-bold">
              <Link
                to={daHref}
                className={`px-2 py-1 transition-all ${
                  lang === 'da' ? 'bg-sky-600 text-white font-black pointer-events-none' : 'text-slate-600'
                }`}
                aria-label="Dansk"
              >
                DA
              </Link>
              <Link
                to={enHref}
                className={`px-2 py-1 transition-all ${
                  lang === 'en' ? 'bg-sky-600 text-white font-black pointer-events-none' : 'text-slate-600'
                }`}
                aria-label="English"
              >
                EN
              </Link>
            </div>

            <a
              href="tel:+4525303016"
              className="text-xs sm:text-sm font-extrabold text-slate-900 hover:text-sky-600 transition-colors whitespace-nowrap hidden sm:inline"
              aria-label={t.nav.callUs}
            >
              +45 25 30 30 16
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-800 hover:text-sky-600 transition-colors"
              aria-label={mobileMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-2xl' : 'fa-bars text-2xl'}`}></i>
            </button>
          </div>
        </div>

        {/* Desktop Navigation Row with Slanted Background (#0863b3) */}
        <div className="hidden min-[1200px]:block pt-1">
          <div className="relative inline-block py-2.5 px-6 pr-12">
            {/* Background layer with slant (isolated so clip-path does not clip the dropdown) */}
            <div className="absolute inset-0 bg-[#0863b3] [clip-path:polygon(-2000px_0,100%_0,calc(100%-24px)_100%,-2000px_100%)] before:content-[''] before:absolute before:top-0 before:bottom-0 before:right-full before:w-[100vw] before:bg-[#0863b3] pointer-events-none shadow-sm" />

            <nav className="relative z-10 flex items-center gap-8 uppercase tracking-wider text-xs font-bold text-white">
              <Link
                to={getPageUrl('home')}
                className={`transition-colors py-1 border-b-2 ${
                  isCurrentActive('home')
                    ? 'text-white border-white'
                    : 'text-sky-100 border-transparent hover:text-white'
                }`}
              >
                {t.nav.home}
              </Link>

              {/* Transport Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <Link
                  to={getPageUrl('services')}
                  className={`flex items-center gap-1.5 transition-colors py-1 border-b-2 ${
                    isCurrentActive('services')
                      ? 'text-white border-white'
                      : 'text-sky-100 border-transparent hover:text-white'
                  }`}
                >
                  <span>{t.nav.services}</span>
                  <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </Link>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 w-64 bg-white rounded-none shadow-2xl border border-slate-200 py-2 text-slate-800 z-50">
                    {transportSubmenu.map((sub) => (
                      <Link
                        key={sub.key}
                        to={sub.path}
                        className={`block px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                          location.pathname === sub.path
                            ? 'text-sky-600 font-bold bg-sky-50/50'
                            : 'text-slate-700 hover:bg-slate-50 hover:text-sky-600'
                        }`}
                      >
                        {sub.title}
                      </Link>
                    ))}
                    <div className="border-t border-slate-100 my-1 pt-1">
                      <Link
                        to={getPageUrl('services')}
                        className="block px-4 py-2 text-[11px] font-bold text-sky-600 hover:bg-sky-50 uppercase tracking-wider"
                      >
                        {t.nav.allServices} →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to={getPageUrl('gallery')}
                className={`transition-colors py-1 border-b-2 ${
                  isCurrentActive('gallery')
                    ? 'text-white border-white'
                    : 'text-sky-100 border-transparent hover:text-white'
                }`}
              >
                {t.nav.gallery}
              </Link>

              <Link
                to={getPageUrl('about')}
                className={`transition-colors py-1 border-b-2 ${
                  isCurrentActive('about')
                    ? 'text-white border-white'
                    : 'text-sky-100 border-transparent hover:text-white'
                }`}
              >
                {t.nav.about}
              </Link>

              <Link
                to={getPageUrl('contact')}
                className={`transition-colors py-1 border-b-2 ${
                  isCurrentActive('contact')
                    ? 'text-white border-white'
                    : 'text-sky-100 border-transparent hover:text-white'
                }`}
              >
                {t.nav.contact}
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu (Visible under 1200px when toggled) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 space-y-3 shadow-xl max-h-[85vh] overflow-y-auto uppercase tracking-wider text-xs font-bold animate-fadeIn">
          {/* Language selection in mobile menu */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 mb-2">
            <span className="text-slate-600 font-semibold normal-case text-xs">{t.nav.switchLang}:</span>
            <div className="flex items-center gap-1">
              <Link
                to={daHref}
                className={`px-3 py-1.5 rounded-none font-bold text-xs ${
                  lang === 'da' ? 'bg-sky-600 text-white shadow-sm pointer-events-none' : 'bg-white text-slate-700 border border-slate-200'
                }`}
              >
                🇩🇰 Dansk
              </Link>
              <Link
                to={enHref}
                className={`px-3 py-1.5 rounded-none font-bold text-xs ${
                  lang === 'en' ? 'bg-sky-600 text-white shadow-sm pointer-events-none' : 'bg-white text-slate-700 border border-slate-200'
                }`}
              >
                🇬🇧 English
              </Link>
            </div>
          </div>

          <Link
            to={getPageUrl('home')}
            className={`block px-3 py-2.5 transition-colors ${
              isCurrentActive('home') ? 'text-sky-600 font-extrabold' : 'text-slate-800 hover:text-sky-600'
            }`}
          >
            {t.nav.home}
          </Link>

          <div>
            <div className="flex items-center justify-between px-3 py-2.5 text-slate-800">
              <Link
                to={getPageUrl('services')}
                className={`transition-colors ${
                  isCurrentActive('services') ? 'text-sky-600 font-extrabold' : 'text-slate-800 hover:text-sky-600'
                }`}
              >
                {t.nav.services}
              </Link>
              <button
                onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}
                className="p-1 text-slate-500 hover:text-sky-600"
                aria-label={t.nav.openMenu}
              >
                <i className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${mobileSubmenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
            </div>

            {mobileSubmenuOpen && (
              <div className="pl-4 space-y-1.5 mt-1 border-l-2 border-sky-100 ml-3">
                {transportSubmenu.map((sub) => (
                  <Link
                    key={sub.key}
                    to={sub.path}
                    className={`block px-3 py-2 transition-colors ${
                      location.pathname === sub.path ? 'text-sky-600 font-extrabold' : 'text-slate-600 hover:text-sky-600'
                    }`}
                  >
                    {sub.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to={getPageUrl('gallery')}
            className={`block px-3 py-2.5 transition-colors ${
              isCurrentActive('gallery') ? 'text-sky-600 font-extrabold' : 'text-slate-800 hover:text-sky-600'
            }`}
          >
            {t.nav.gallery}
          </Link>

          <Link
            to={getPageUrl('about')}
            className={`block px-3 py-2.5 transition-colors ${
              isCurrentActive('about') ? 'text-sky-600 font-extrabold' : 'text-slate-800 hover:text-sky-600'
            }`}
          >
            {t.nav.about}
          </Link>

          <Link
            to={getPageUrl('contact')}
            className={`block px-3 py-2.5 transition-colors ${
              isCurrentActive('contact') ? 'text-sky-600 font-extrabold' : 'text-slate-800 hover:text-sky-600'
            }`}
          >
            {t.nav.contact}
          </Link>

          <div className="pt-4 border-t border-slate-100 space-y-3">
            {/* Social Media Links inside Mobile Menu */}
            <div className="flex items-center justify-center gap-4 py-2 border-b border-slate-100 mb-2">
              <span className="text-slate-400 font-semibold text-[11px] uppercase tracking-wider">Socials:</span>
              <a 
                href="https://www.facebook.com/M.RouTransport/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:bg-sky-500 hover:text-white transition-colors"
              >
                <i className="fa-brands fa-facebook-f"></i>
              </a>
              <a 
                href="https://www.instagram.com/m.rou_transport/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:bg-sky-500 hover:text-white transition-colors"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>
              <a 
                href="https://www.tiktok.com/@mroutransport" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:bg-sky-500 hover:text-white transition-colors"
              >
                <i className="fa-brands fa-tiktok"></i>
              </a>
            </div>

            <a
              href="tel:+4525303016"
              className="flex items-center justify-center gap-2 bg-slate-900 text-white font-bold py-3 rounded-none w-full text-center whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-400 text-sm"></i>
              <span className="whitespace-nowrap">+45 25 30 30 16</span>
            </a>

            <Link
              to={getPageUrl('contact')}
              className="block bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-none w-full text-center shadow-md hover:-translate-y-1 active:translate-y-0 transition-all"
            >
              {t.nav.contactCta}
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
